import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LoginOutlined, UserOutlined } from '@ant-design/icons';
import { User } from '@app/types/api';
import { Flex, Form, Input, Button, Divider, Typography } from '@app/ui';
import connect from '@app/utils/connect';
import userActions from '@app/actions/user';

export type LoginFormProps = {
    user: User;
    isPopulatingClients: boolean;
    initForgotPasswordFlow: () => void;
    initLoginFlow: (values: { email: string; password: string }) => void;
};

export const LoginForm = ({
    initForgotPasswordFlow,
    initLoginFlow,
    isPopulatingClients,
    user,
}: LoginFormProps) => {
    const { t } = useTranslation('login');
    const [form] = Form.useForm();

    const { error, isLoading } = user;

    const hasError = useMemo(() => {
        return error && typeof error === 'object' && !!error.message;
    }, [error]);

    const hasSsoError = useMemo(() => {
        return error && typeof error === 'object' && error.ssoError;
    }, [error]);

    return (
        <Flex vertical className="-m-t-2 -form">
            <Form
                name="login"
                form={form}
                layout="vertical"
                autoComplete="off"
                onFinish={initLoginFlow}
            >
                <Form.Item
                    label={t('form.emailLabel')}
                    name="email"
                    validateTrigger="onBlur"
                    validateStatus={
                        hasError && !hasSsoError ? 'error' : undefined
                    }
                    help={
                        hasError && !hasSsoError && error.message
                            ? error.message
                            : undefined
                    }
                    rules={[
                        {
                            type: 'email',
                            message: t('form.validation.emailInvalid'),
                        },
                        {
                            min: 3,
                            message: t('form.validation.emailRequired'),
                        },
                    ]}
                >
                    <Input
                        placeholder={t('form.emailPlaceholder')}
                        type="email"
                        prefix={<UserOutlined />}
                    />
                </Form.Item>

                <Form.Item
                    validateTrigger="onBlur"
                    name="password"
                    label={t('form.passwordLabel')}
                    className="-m-b-3"
                    validateStatus={
                        hasError && !hasSsoError ? 'error' : undefined
                    }
                    help={
                        hasError && !hasSsoError && error.message
                            ? error.message
                            : undefined
                    }
                    rules={[
                        {
                            min: 3,
                            message: t('form.validation.passwordRequired'),
                        },
                    ]}
                >
                    <Input.Password
                        placeholder={t('form.passwordPlaceholder')}
                        type="password"
                    />
                </Form.Item>

                <Button onClick={initForgotPasswordFlow} type="link">
                    {t('form.forgotPassword')}
                </Button>

                <Form.Item>
                    <Button
                        type="primary"
                        icon={<LoginOutlined />}
                        loading={isPopulatingClients || isLoading}
                        iconPosition="end"
                        htmlType="submit"
                    >
                        {t('form.logIn')}
                    </Button>
                </Form.Item>
            </Form>

            <Divider />

            <Flex align="center" justify="center">
                <Typography.Text>
                    {t('form.takeMeToCensus')}{' '}
                    <a
                        target="_blank"
                        href="https://census.relometrics.com/login"
                        rel="noreferrer"
                    >
                        {t('form.census')}
                    </a>
                </Typography.Text>
            </Flex>
        </Flex>
    );
};

export default connect(['user'], userActions)(LoginForm);
